import { useState, useMemo, useEffect } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card, CardContent } from "../components/ui/card";
import { ACCOUNTING_API_BASE_URL } from "../config";
import initialAccountingData from "../data/accounting_invoices.json";
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  Download,
  FileSpreadsheet,
  Search,
  Filter,
  RefreshCw,
  FolderArchive,
  ArrowUpDown,
  ExternalLink,
  ShieldCheck,
  Zap,
  Globe,
  Server,
  Loader2,
  X,
  ArrowRight,
  UserCheck,
  Copy,
  Check,
  MessageSquare,
  Palette,
  Edit3,
  Tag,
  Sparkles,
  Plus,
  CornerDownLeft
} from "lucide-react";

export type RowColor = "green" | "yellow" | "blue" | "red" | "purple" | "orange" | "gray" | "none";

interface BilagItem {
  bilag: number | string;
  date: string;
  text: string;
  amount: number;
  currency: string;
  vendor: string;
  status: "Found" | "Missing" | "Note";
  status_raw?: string;
  status_detail?: string;
  filename?: string;
  comment?: string;
  color?: RowColor;
}

export const COLOR_CONFIG: Record<RowColor, { label: string; name: string; bgBadge: string; textBadge: string; rowBg: string; borderLeft: string; dot: string; hex: string }> = {
  green: {
    label: "Grøn",
    name: "Godkendt / Bilag fundet",
    bgBadge: "bg-emerald-100",
    textBadge: "text-emerald-800 border-emerald-300",
    rowBg: "bg-emerald-50/70 hover:bg-emerald-100/60",
    borderLeft: "border-l-4 border-l-emerald-500",
    dot: "bg-emerald-500",
    hex: "#C6EFCE"
  },
  yellow: {
    label: "Gul",
    name: "Self-invoice / Afregning / Note",
    bgBadge: "bg-amber-100",
    textBadge: "text-amber-800 border-amber-300",
    rowBg: "bg-amber-50/70 hover:bg-amber-100/60",
    borderLeft: "border-l-4 border-l-amber-500",
    dot: "bg-amber-400",
    hex: "#FFF2CC"
  },
  blue: {
    label: "Blå",
    name: "Info / Skal tjekkes / Partner",
    bgBadge: "bg-sky-100",
    textBadge: "text-sky-800 border-sky-300",
    rowBg: "bg-sky-50/70 hover:bg-sky-100/60",
    borderLeft: "border-l-4 border-l-sky-500",
    dot: "bg-sky-500",
    hex: "#D9E1F2"
  },
  red: {
    label: "Rød",
    name: "Mangler bilag / Haster",
    bgBadge: "bg-rose-100",
    textBadge: "text-rose-800 border-rose-300",
    rowBg: "bg-rose-50/70 hover:bg-rose-100/60",
    borderLeft: "border-l-4 border-l-rose-500",
    dot: "bg-rose-500",
    hex: "#FCE4D6"
  },
  purple: {
    label: "Lilla",
    name: "Privat udlæg / Særlig post",
    bgBadge: "bg-purple-100",
    textBadge: "text-purple-800 border-purple-300",
    rowBg: "bg-purple-50/70 hover:bg-purple-100/60",
    borderLeft: "border-l-4 border-l-purple-500",
    dot: "bg-purple-500",
    hex: "#E1D5E7"
  },
  orange: {
    label: "Orange",
    name: "Regulering / Afvigelse",
    bgBadge: "bg-orange-100",
    textBadge: "text-orange-800 border-orange-300",
    rowBg: "bg-orange-50/70 hover:bg-orange-100/60",
    borderLeft: "border-l-4 border-l-orange-500",
    dot: "bg-orange-500",
    hex: "#FFE6CC"
  },
  gray: {
    label: "Grå",
    name: "Lukket / Arkiveret",
    bgBadge: "bg-gray-200",
    textBadge: "text-gray-800 border-gray-300",
    rowBg: "bg-gray-100/80 hover:bg-gray-200/60",
    borderLeft: "border-l-4 border-l-gray-400",
    dot: "bg-gray-400",
    hex: "#F2F2F2"
  },
  none: {
    label: "Standard",
    name: "Ingen markering",
    bgBadge: "bg-gray-100",
    textBadge: "text-gray-600 border-gray-200",
    rowBg: "hover:bg-gray-50/80",
    borderLeft: "border-l-4 border-l-transparent",
    dot: "bg-gray-300 border border-gray-400",
    hex: "transparent"
  }
};

const NOTE_PRESETS = [
  "Afregningsbilag / Self-invoice",
  "Intern overførsel",
  "Offentlig refusion (Barsel.dk)",
  "Skattekonto indbetaling",
  "Privat udlæg",
  "Afventer bilag fra leverandør",
  "Momsfritaget",
  "Afstemt med Jesper"
];

interface SummaryStats {
  total_count: number;
  found_count: number;
  missing_count: number;
  total_missing_amount: number;
  total_rows?: number;
  missing_amount?: number;
  currency?: string;
  vendor_breakdown?: Record<string, { total: number; found: number; missing: number }>;
}

export default function AccountingInvoicesPage() {
  const initialSummary: SummaryStats = {
    total_count: (initialAccountingData.summary as any).total_count || (initialAccountingData.summary as any).total_rows || 300,
    found_count: (initialAccountingData.summary as any).found_count || 0,
    missing_count: (initialAccountingData.summary as any).missing_count || 0,
    total_missing_amount: (initialAccountingData.summary as any).missing_amount || (initialAccountingData.summary as any).total_missing_amount || 0,
    currency: (initialAccountingData.summary as any).currency || "DKK"
  };

  const [data, setData] = useState<BilagItem[]>(initialAccountingData.rows as unknown as BilagItem[]);
  const [stats, setStats] = useState<SummaryStats>(initialSummary);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: "success" | "info" | "error" } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | "Found" | "Missing">("All");
  const [vendorFilter, setVendorFilter] = useState("All");
  const [colorFilter, setColorFilter] = useState<"All" | RowColor>("All");
  const [onlyWithNotesFilter, setOnlyWithNotesFilter] = useState(false);

  // Selection & Bulk actions
  const [selectedBilags, setSelectedBilags] = useState<Array<string | number>>([]);
  const [bulkNoteInput, setBulkNoteInput] = useState("");
  const [isBulkNoteOpen, setIsBulkNoteOpen] = useState(false);

  // Editing comments & Color picker states
  const [editingBilag, setEditingBilag] = useState<string | number | null>(null);
  const [tempComment, setTempComment] = useState("");
  const [activeColorMenuBilag, setActiveColorMenuBilag] = useState<string | number | null>(null);

  // Multi-Account Scraper Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProvider, setModalProvider] = useState("GetResponse");
  const [modalStep, setModalStep] = useState<"PROMPT" | "DOWNLOADING" | "COMPLETED">("PROMPT");
  const [matchedInSession, setMatchedInSession] = useState(0);
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
  const [copiedScript, setCopiedScript] = useState(false);
  const [isExtensionConnected, setIsExtensionConnected] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState("2026-04-01");
  const [filterEndDate, setFilterEndDate] = useState("2026-07-31");

  const PROVIDER_ACCOUNTS: Record<string, Array<{ name: string; desc: string }>> = {
    GetResponse: [
      { name: "Primary Account (post@sparkredit.se / kristoffer)", desc: "€469.00 monthly tier" },
      { name: "Finland Account (posti@tarjouksesi.com)", desc: "€259.00 monthly tier" },
      { name: "German Account (post@gewinnspielen.com)", desc: "€259.00 monthly tier" }
    ],
    Brevo: [
      { name: "Primary Account (Kristoffer Frederik Ryberg)", desc: "€50, €100, €150, €269, €369, €519 (7 Invoices)" },
      { name: "Secondary Account (Otto Lund)", desc: "€369.00 Monthly tier (4 Invoices)" }
    ],
    FeedBlitz: [
      { name: "FeedBlitz Main Account", desc: "$299 - $599 monthly (16 Invoices)" }
    ],
    MailerLite: [
      { name: "MailerLite Main Account", desc: "€6.33 - €21.68 monthly (11 Invoices)" }
    ],
    Moosend: [
      { name: "Swiftkredit Account (Kasper Valentin Møller)", desc: "$25.07 - $315.00 monthly (5 Invoices)" }
    ],
    Drip: [
      { name: "Primary Account (Kristoffer Frederik Ryberg)", desc: "$369.00 - $899.00 monthly (4 Invoices)" }
    ],
    Facebook: [
      { name: "Simply Loan Sverige (Account 303440811984313)", desc: "DKK 408.91 - DKK 6,365.77 (15 Invoices)" }
    ],
    Pleo: [
      { name: "Pleo Main Account (Kristoffer Frederik Ryberg)", desc: "DKK 696.68 - DKK 1,650.00 monthly (8 Invoices)" }
    ],
    Google: [
      { name: "Google One / Play (Kristoffer Frederik Ryberg)", desc: "DKK 1,049.00 - DKK 2,099.00 monthly (5 Invoices)" }
    ],
    GoDaddy: [
      { name: "Kristoffer Account (post@afima.dk)", desc: "21 Q2 orders / 28 total (#4053564660, #4055340543, ...)" },
      { name: "Otto Account (Secondary card)", desc: "6 Q2 orders (#64835444, #67387149, #89347122, #91487371...)" }
    ]
  };

  const activeAccounts = PROVIDER_ACCOUNTS[modalProvider] || PROVIDER_ACCOUNTS.GetResponse;

  // Auto-downloader JS snippet per provider
  const getConsoleSnippet = (provider: string) => {
    if (provider.toLowerCase().includes("godaddy")) {
      return `// 1-Click GoDaddy Official PDF Downloader (Universal & Reusable)
async function downloadGoDaddyPDFs() {
  console.log("🚀 Starting GoDaddy Official PDF Downloader for ${filterStartDate} to ${filterEndDate}...");

  function parseDate(str) {
    if (!str) return null;
    const m = str.match(/(\\d{1,2})\\.(\\d{1,2})\\.(\\d{4})/);
    if (m) {
      return \`\${m[3]}-\${m[2].padStart(2, '0')}-\${m[1].padStart(2, '0')}\`;
    }
    const iso = str.match(/(\\d{4})-(\\d{2})-(\\d{2})/);
    if (iso) return iso[0];
    return null;
  }

  // Load html2pdf for direct in-browser download
  if (typeof html2pdf === 'undefined') {
    await new Promise(r => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      s.onload = r;
      s.onerror = r;
      document.head.appendChild(s);
    });
  }

  // Paginate backwards until startDate is visible
  for (let attempt = 0; attempt < 15; attempt++) {
    const rows = Array.from(document.querySelectorAll('.receipt-row, [data-eid*="receipt_row"], [class*="receipt-row"]'));
    const dates = rows.map(r => parseDate(r.innerText)).filter(Boolean);
    const earliest = dates.length ? dates.reduce((a, b) => a < b ? a : b) : null;
    if (earliest && earliest <= '${filterStartDate}') break;
    const loadMoreBtn = Array.from(document.querySelectorAll('button, a')).find(b => {
      const t = (b.innerText || '').toLowerCase();
      const eid = b.getAttribute('data-eid') || '';
      return eid.includes('load_more') || t.includes('indlæs mere') || t.includes('load more');
    });
    if (loadMoreBtn && loadMoreBtn.offsetParent !== null) {
      loadMoreBtn.click();
      await new Promise(r => setTimeout(r, 2200));
    } else {
      break;
    }
  }

  const allRows = Array.from(document.querySelectorAll('.receipt-row, [data-eid*="receipt_row"], [class*="receipt-row"]'));
  const matching = [];
  const seen = new Set();
  for (const r of allRows) {
    const d = parseDate(r.innerText);
    if (d && (d < '${filterStartDate}' || d > '${filterEndDate}')) continue;
    const btn = r.querySelector('.order-number button, button.px-link, a');
    const chk = r.querySelector('input[type="checkbox"]');
    const id = (chk?.id || '').match(/\\d{8,10}/)?.[0] || (r.innerText.match(/\\b(\\d{8,10})\\b/)?.[1] || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    matching.push({ row: r, btn: btn || r, id, date: d || '2026' });
  }

  console.log(\`🎯 Found \${matching.length} matching GoDaddy orders in date range.\`);
  let count = 0;
  for (let i = 0; i < matching.length; i++) {
    const { row, btn, id, date } = matching[i];
    console.log(\`[\${i+1}/\${matching.length}] Opening Order #\${id}...\`);
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await new Promise(r => setTimeout(r, 300));
    btn.click();
    await new Promise(r => setTimeout(r, 1600));

    const modal = document.querySelector('.receipt-view, [class*="receipt-view"], [role="dialog"], .modal-content') || document.body;
    const fullHtml = \`<!DOCTYPE html><html><head><base href="https://account.godaddy.com">\${document.head.innerHTML}</head><body>\${modal.outerHTML}</body></html>\`;

    try {
      await fetch("http://localhost:8000/api/accounting/godaddy_receipts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ receipts: [{ orderId: id, date, html: fullHtml }] })
      });
    } catch(e) {}

    if (window.html2pdf) {
      try {
        await html2pdf().set({
          margin: [10, 10, 10, 10],
          filename: \`GoDaddy_Official_Receipt_\${id}.pdf\`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }).from(modal).save();
      } catch(e) {}
    }
    count++;

    const closeBtn = document.querySelector('[aria-label*="Close"], [aria-label*="Luk"], button[class*="close"], [data-eid*="close"]')?.closest('button');
    if (closeBtn) closeBtn.click();
    else document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true }));
    await new Promise(r => setTimeout(r, 800));
  }
  alert(\`✓ Færdig! \${count} GoDaddy PDF-bilag er gemt og afstemt i dit regnskab!\`);
}
downloadGoDaddyPDFs();`;
    }
    if (provider.toLowerCase().includes("google")) {
      return `// 1-Click Gmail Google Play Receipt Downloader & Matcher
async function captureGooglePlayReceipts() {
  console.log("🚀 Starting Google Play / Google One Receipt Capture...");
  const rows = Array.from(document.querySelectorAll('tr[role="row"], div[role="row"]')).filter(r => {
    const text = r.innerText || '';
    return text.includes('Google Play') || text.includes('ordrekvittering') || text.includes('Google One');
  });

  console.log("Found " + rows.length + " candidate Google receipt emails.");
  const receipts = [];
  for (let i = 0; i < Math.min(rows.length, 6); i++) {
    const row = rows[i];
    row.click();
    await new Promise(r => setTimeout(r, 1500));

    const emailBody = document.querySelector('.a3s.aiL, [data-message-id], .adn.ads') || document.querySelector('div[role="main"]');
    const fullText = document.body.innerText;
    const dateMatch = fullText.match(/(\\d{1,2}\\.\\s+[a-zæøåA-ZÆØÅ]{3,4}\\.?\\s+\\d{4})/i) || fullText.match(/(\\d{4}-\\d{2}-\\d{2})/);
    const amtMatch = fullText.match(/([\\d\\.,]+)\\s*(?:kr|DKK)/i) || fullText.match(/(?:kr|DKK)\\s*([\\d\\.,]+)/i);

    if (emailBody) {
      receipts.push({
        date: dateMatch ? dateMatch[0] : '2026',
        amt: amtMatch ? amtMatch[0] : '1699 DKK',
        html: emailBody.innerHTML
      });
      console.log("  ✓ Captured receipt " + (i+1) + ": " + (dateMatch?.[0] || '') + " " + (amtMatch?.[0] || ''));
    }

    const backBtn = document.querySelector('div[aria-label*="Tilbage"], div[aria-label*="Back"], [data-tooltip*="Tilbage"]');
    if (backBtn) {
      backBtn.click();
      await new Promise(r => setTimeout(r, 1200));
    }
  }

  console.log("Sending " + receipts.length + " Google receipts to backend to generate PDFs & match...");
  const res = await fetch("http://localhost:8000/api/accounting/google_receipts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipts })
  });

  console.log("🎉 Done! Backend result:", await res.json());
}
captureGooglePlayReceipts();`;
    }
    if (provider.toLowerCase().includes("pleo")) {
      return `// 1-Click Pleo Invoices Auto-Downloader
async function downloadAllPleoInvoices() {
  const downloadBtns = Array.from(document.querySelectorAll('a[href*="download"], a[href*="invoice"], a[href*="receipt"], button')).filter(b => {
    const t = (b.innerText || '').toLowerCase();
    return t.includes('download') || t.includes('pdf') || t.includes('hent');
  });
  console.log("Found " + downloadBtns.length + " Pleo download buttons. Triggering...");
  for (let i = 0; i < downloadBtns.length; i++) {
    downloadBtns[i].click();
    await new Promise(r => setTimeout(r, 1200));
  }
  console.log("Finished triggering Pleo downloads!");
}
downloadAllPleoInvoices();`;
    }
    if (provider.toLowerCase().includes("facebook")) {
      return `// Facebook / Meta Ads Bulk Invoices:
// 1. In Meta Business Suite, pick your date range (e.g. Q2 2026).
// 2. Click "Download All" or "Export Report" -> Save the Transactions.zip to Downloads.
// 3. Click "Scan & Match Invoices" on the dashboard — the backend will unzip and pair all 15 reference IDs automatically!`;
    }
    if (provider.toLowerCase().includes("drip")) {
      return `// 1-Click Drip Invoice Auto-Downloader
async function downloadAllDripInvoices() {
  const items = Array.from(document.querySelectorAll('li[data-role="list-item"]')).filter(li => {
    const note = li.querySelector('.boomlist__note')?.innerText || '';
    const d = new Date(note);
    const iso = isNaN(d) ? '' : d.toISOString().slice(0, 10);
    return iso >= '${filterStartDate}' && iso <= '${filterEndDate}';
  });
  console.log("Found " + items.length + " Drip receipts to capture...");
  const receipts = [];
  for (const item of items) {
    const note = item.querySelector('.boomlist__note')?.innerText || '';
    const amt = item.querySelector('.boomlist__data li')?.innerText || '';
    const link = item.querySelector('a[href*="/receipts/"]');
    if (link) {
      console.log("Fetching " + note + " (" + amt + ")...");
      const res = await fetch(link.href);
      const html = await res.text();
      receipts.push({ url: link.href, date: note, amt: amt, html: html });
    }
  }
  console.log("Sending " + receipts.length + " receipts to local backend to generate PDFs...");
  const res = await fetch("http://localhost:8000/api/accounting/drip_receipts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipts })
  });
  console.log("Finished! Matched response:", await res.json());
}
downloadAllDripInvoices();`;
    }
    if (provider.toLowerCase().includes("moosend")) {
      return `// 1-Click Moosend Invoice Auto-Downloader
async function downloadAllMoosendInvoices() {
  const pdfButtons = Array.from(document.querySelectorAll("button")).filter(b => b.innerText.trim().toLowerCase() === "pdf");
  console.log("Found " + pdfButtons.length + " PDF buttons. Starting download...");
  for (let i = 0; i < pdfButtons.length; i++) {
    const btn = pdfButtons[i];
    const row = btn.closest(".huUtpq, [class*='sc-kMribo']") || btn.parentElement?.parentElement;
    const text = row ? row.innerText : "";
    const dateMatch = text.match(/\\d{4}-\\d{2}-\\d{2}/);
    const date = dateMatch ? dateMatch[0] : "";
    if (date >= '${filterStartDate}' && date <= '${filterEndDate}') {
      console.log("✓ Clicking PDF for: " + text.replace(/\\n/g, ' '));
      btn.click();
      await new Promise(r => setTimeout(r, 2000));
    }
  }
  console.log("All Moosend invoices triggered!");
}
downloadAllMoosendInvoices();`;
    }
    if (provider.toLowerCase().includes("brevo") || provider.toLowerCase().includes("sendinblue")) {
      return `// 1-Click Brevo Invoice Auto-Downloader
async function downloadAllBrevoInvoices() {
  const rows = Array.from(document.querySelectorAll("table tbody tr")).filter(r => r.innerText.includes("2026") || r.innerText.includes("/26"));
  console.log("Downloading " + rows.length + " Brevo invoices...");
  for (let i = 0; i < rows.length; i++) {
    const dots = rows[i].querySelector('button[aria-label="action-list"], button');
    if (!dots) continue;
    dots.click();
    await new Promise(r => setTimeout(r, 400));
    const btn = document.querySelector('button#download, button[role="menuitem"], [class*="sib-action-list__item"] button');
    if (btn) btn.click();
    await new Promise(r => setTimeout(r, 1800));
  }
  console.log("All Brevo invoices downloaded!");
}
downloadAllBrevoInvoices();`;
    }
    if (provider.toLowerCase().includes("mailerlite")) {
      return `// 1-Click MailerLite Invoice Downloader
const rows = Array.from(document.querySelectorAll('tr[data-test-id="invoice-row"]')).filter(r => {
  const d = r.innerText.match(/\\d{4}-\\d{2}-\\d{2}/)?.[0] || '';
  return d >= '${filterStartDate}' && d <= '${filterEndDate}';
});
console.log('Downloading ' + rows.length + ' MailerLite invoices...');
rows.forEach((r, i) => {
  const a = r.querySelector('a[data-test-id="download-invoice"], a[href*="/pdf"], a[href*="/invoices/"]');
  if (a) {
    setTimeout(() => {
      let url = a.href;
      if (!url.includes('/pdf') && url.includes('/invoices/')) {
        const parts = url.split('?');
        url = parts[0] + '/pdf' + (parts[1] ? '?' + parts[1] : '');
      }
      const ifr = document.createElement('iframe');
      ifr.style.display = 'none';
      ifr.src = url;
      document.body.appendChild(ifr);
      console.log('✓ Triggered (' + (i + 1) + '/' + rows.length + ')');
      setTimeout(() => ifr.remove(), 12000);
    }, i * 1000);
  }
});`;
    }
    return `// 1-Click GetResponse Invoice Auto-Downloader
const links = Array.from(document.querySelectorAll('a[href*="/app/document/"], a[data-ats-billing-history="individual-invoice-url"]')).map(a => a.href);
console.log("Found " + links.length + " invoice links. Opening...");
links.forEach((url, i) => {
  setTimeout(() => window.open(url, '_blank'), i * 800);
});`;
  };

  const currentSnippet = getConsoleSnippet(modalProvider);

  // Listen for extension events
  useEffect(() => {
    const handleExtensionMsg = (event: MessageEvent) => {
      if (event.data?.type === "AFIMA_EXTENSION_READY") {
        setIsExtensionConnected(true);
      }
      if (event.data?.type === "AFIMA_SCRAPER_RESULT") {
        if (event.data.success) {
          setTimeout(async () => {
            try {
              const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/match`, { method: "POST" });
              if (res.ok) {
                const json = await res.json();
                if (json.rows) setData(json.rows);
                if (json.summary) setStats(json.summary);
                setMatchedInSession(event.data.data?.count || 7);
              }
            } catch (e) {}
            setModalStep("COMPLETED");
          }, 3500);
        }
      }
    };
    window.addEventListener("message", handleExtensionMsg);
    return () => window.removeEventListener("message", handleExtensionMsg);
  }, []);

  // Load initial data
  const loadData = async () => {
    try {
      const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/invoices?action=list`);
      if (res.ok) {
        const json = await res.json();
        if (json.rows && json.rows.length > 0) {
          setData(json.rows);
          if (json.summary) {
            setStats(json.summary);
          }
        }
      }
    } catch (e) {
      console.log("Could not fetch accounting data from server:", e);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Poll for newly downloaded PDFs when modal is open
  useEffect(() => {
    if (!isModalOpen) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/match`, { method: "POST" });
        if (res.ok) {
          const json = await res.json();
          if (json.rows) setData(json.rows);
          if (json.summary) setStats(json.summary);
        }
      } catch (e) {
        // quiet poll
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [isModalOpen]);

  // Listen for extension scraper results & bridge events
  useEffect(() => {
    const handleBridgeMessage = (event: MessageEvent) => {
      if (event.source !== window || !event.data) return;

      if (event.data.type === "AFIMA_SCRAPER_RESULT") {
        const { success, data, error } = event.data;
        if (success) {
          const count = data?.count || 0;
          setMatchedInSession(count);
          setModalStep("COMPLETED");
          loadData();
          setNotification({
            message: `✅ Scraper finished! Processed ${count} receipts.`,
            type: "success"
          });
        } else if (error) {
          setNotification({
            message: `⚠️ Scraper: ${error}`,
            type: "error"
          });
          setModalStep("PROMPT");
        }
      }
    };

    window.addEventListener("message", handleBridgeMessage);
    return () => window.removeEventListener("message", handleBridgeMessage);
  }, []);

  // 1. Open Browser Window & Show Modal
  const handleOpenScraperModal = (provider: string) => {
    setModalProvider(provider);
    setModalStep("PROMPT");
    setIsModalOpen(true);
    setMatchedInSession(0);
    setCopiedScript(false);

    const url =
      provider.toLowerCase() === "getresponse"
        ? "https://secure.getresponse.com/app/history/"
        : provider.toLowerCase() === "brevo"
        ? "https://app.brevo.com/billing/account/plans/billing-history"
        : provider.toLowerCase() === "feedblitz"
        ? "https://app.feedblitz.com/f/?Billing"
        : provider.toLowerCase() === "moosend"
        ? "https://swiftkredit.moosend.com/billing/overview"
        : provider.toLowerCase() === "drip"
        ? "https://www.getdrip.com/8284673/settings/billing?section=payment-history"
        : provider.toLowerCase() === "facebook"
        ? "https://business.facebook.com/billing_hub/payment_activity"
        : provider.toLowerCase() === "pleo"
        ? "https://app.pleo.io/settings/billing"
        : provider.toLowerCase() === "google"
        ? "https://mail.google.com/mail/u/0/#category/purchases"
        : provider.toLowerCase() === "godaddy"
        ? "https://account.godaddy.com/receipts"
        : "https://dashboard.mailerlite.com/billing/invoices";

    window.open(url, `${provider}Popup`, "width=1280,height=850,resizable=yes,scrollbars=yes,location=yes");
  };

  // Copy Snippet
  const handleCopySnippet = () => {
    navigator.clipboard.writeText(currentSnippet);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 3000);
  };

  const [isRunningMatcher, setIsRunningMatcher] = useState(false);

  // Dedicated Universal Matcher
  const handleRunUniversalMatcher = async () => {
    setIsRunningMatcher(true);
    setNotification({
      message: "Scanning Downloads folder and inbox_pdf for invoices & receipts...",
      type: "info",
    });

    try {
      const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/match`, {
        method: "POST",
      });
      if (res.ok) {
        const json = await res.json();
        if (json.rows) setData(json.rows);
        if (json.summary) {
          const newlyFound = json.summary.found_count - stats.found_count;
          setStats(json.summary);
          if (newlyFound > 0) {
            setNotification({
              message: `✅ Matched ${newlyFound} new bilag! Total matched is now ${json.summary.found_count} / ${json.summary.total_count} (${Math.round((json.summary.found_count / json.summary.total_count) * 100)}%).`,
              type: "success",
            });
          } else {
            setNotification({
              message: `Scan complete! Scanned Downloads & inbox_pdf (${json.summary.found_count} bilag matched).`,
              type: "info",
            });
          }
        }
      } else {
        setNotification({
          message: "Matcher server returned an error.",
          type: "error",
        });
      }
    } catch (e) {
      console.error("Run Matcher error:", e);
      setNotification({
        message: "⚠️ Could not connect to local server on port 8000. Make sure the local python server is running.",
        type: "error",
      });
    } finally {
      setIsRunningMatcher(false);
    }
  };

  // 2. User presses "Start Download & Match"
  const handleStartDownloadAndMatch = async () => {
    setModalStep("DOWNLOADING");

    setNotification({
      message: `🚀 Starting ${modalProvider} auto-downloader for ${filterStartDate} to ${filterEndDate}...`,
      type: "info"
    });

    // Trigger Chrome Extension for all providers including GoDaddy
    window.postMessage({
      type: "AFIMA_TRIGGER_SCRAPER",
      provider: modalProvider,
      startDate: filterStartDate,
      endDate: filterEndDate
    }, "*");

    // Trigger backend scan after delay to let downloads land
    const delay = modalProvider.toLowerCase().includes("godaddy") ? 15000 : modalProvider.toLowerCase().includes("moosend") ? 11000 : 5000;
    setTimeout(async () => {
      let count = 0;
      try {
        const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/match`, { method: "POST" });
        if (res.ok) {
          const json = await res.json();
          if (json.rows) setData(json.rows);
          if (json.summary) {
            count = json.summary.found_count - stats.found_count;
            if (count > 0) setMatchedInSession(count);
            setStats(json.summary);
          }
        }
      } catch (e) {
        console.error("Match error:", e);
      }
      loadData();
    }, delay);
  };

  // 3. User clicks "Next Account"
  const handleNextAccount = () => {
    const nextIdx = (currentAccountIndex + 1) % activeAccounts.length;
    setCurrentAccountIndex(nextIdx);
    setModalStep("PROMPT");
    const url =
      modalProvider.toLowerCase() === "getresponse"
        ? "https://secure.getresponse.com/app/history/"
        : modalProvider.toLowerCase() === "brevo"
        ? "https://app.brevo.com/billing/account/plans/billing-history"
        : modalProvider.toLowerCase() === "feedblitz"
        ? "https://app.feedblitz.com/f/?Billing"
        : "https://dashboard.mailerlite.com/billing/invoices";
    window.open(url, `${modalProvider}Popup`, "width=1280,height=850,resizable=yes,scrollbars=yes,location=yes");
  };

  // Sync API Providers (GoDaddy, Adtraction, AWS)
  const handleSyncProvider = async (provider: string) => {
    setIsSyncing(provider);
    setNotification({
      message: `Syncing ${provider} orders and invoices via direct API...`,
      type: "info"
    });

    try {
      const res = await fetch(
        `${ACCOUNTING_API_BASE_URL}/api/accounting/invoices?action=sync&provider=${encodeURIComponent(
          provider.toLowerCase()
        )}`
      );
      if (res.ok) {
        const json = await res.json();
        if (json.rows) setData(json.rows);
        if (json.summary) setStats(json.summary);
        setNotification({
          message: `Sync completed for ${provider}! Invoices matched and Excel rows marked green.`,
          type: "success"
        });
      }
    } catch (e) {
      setNotification({
        message: `Error syncing ${provider}.`,
        type: "error"
      });
    } finally {
      setIsSyncing(null);
    }
  };

  const handleDownloadExcel = () => {
    window.open(`${ACCOUNTING_API_BASE_URL}/api/accounting/export-excel`, "_blank");
  };

  const handleDownloadZip = () => {
    window.open(`${ACCOUNTING_API_BASE_URL}/api/accounting/export-zip`, "_blank");
  };

  const handleDownloadNumberedZip = () => {
    window.open(`${ACCOUNTING_API_BASE_URL}/api/accounting/export-numbered-zip`, "_blank");
  };

  // Update row customization (Comment & Color)
  const handleUpdateRowCustomization = async (
    bilag: string | number,
    newComment?: string,
    newColor?: RowColor
  ) => {
    // Optimistically update in state
    setData((prev) =>
      prev.map((row) => {
        if (row.bilag.toString() === bilag.toString()) {
          const updatedRow: BilagItem = {
            ...row,
            comment: newComment !== undefined ? newComment : row.comment,
            color: newColor !== undefined ? newColor : row.color,
          };
          return updatedRow;
        }
        return row;
      })
    );

    // Save to backend API and Excel
    try {
      const res = await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/update_customization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bilag: bilag.toString(),
          comment: newComment,
          color: newColor,
        }),
      });
      if (res.ok) {
        setNotification({
          message: `Bilag #${bilag} opdateret og gemt i Excel!`,
          type: "success",
        });
        setTimeout(() => setNotification(null), 2500);
      }
    } catch (e) {
      console.log("Could not save customization to backend:", e);
      setNotification({
        message: `Bilag #${bilag} opdateret lokalt.`,
        type: "info",
      });
      setTimeout(() => setNotification(null), 2000);
    }
  };

  // Selection toggle
  const handleToggleSelect = (bilag: string | number) => {
    setSelectedBilags((prev) =>
      prev.includes(bilag) ? prev.filter((b) => b !== bilag) : [...prev, bilag]
    );
  };

  const handleSelectAll = () => {
    if (selectedBilags.length === filteredRows.length && filteredRows.length > 0) {
      setSelectedBilags([]);
    } else {
      setSelectedBilags(filteredRows.map((r) => r.bilag));
    }
  };

  // Bulk set color
  const handleBulkSetColor = async (color: RowColor) => {
    if (selectedBilags.length === 0) return;
    const targetIds = [...selectedBilags];
    setData((prev) =>
      prev.map((row) => {
        if (targetIds.includes(row.bilag)) {
          return { ...row, color };
        }
        return row;
      })
    );

    try {
      const updates = targetIds.map((b) => ({ bilag: b.toString(), color }));
      await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/bulk_customization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      setNotification({
        message: `${targetIds.length} rækker blev farvet ${COLOR_CONFIG[color].label} og gemt i Excel!`,
        type: "success",
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (e) {
      console.log("Bulk update error:", e);
    }
  };

  // Bulk set comment
  const handleBulkSetNote = async (noteText: string) => {
    if (selectedBilags.length === 0 || !noteText.trim()) return;
    const targetIds = [...selectedBilags];
    setData((prev) =>
      prev.map((row) => {
        if (targetIds.includes(row.bilag)) {
          return { ...row, comment: noteText };
        }
        return row;
      })
    );

    try {
      const updates = targetIds.map((b) => ({ bilag: b.toString(), comment: noteText }));
      await fetch(`${ACCOUNTING_API_BASE_URL}/api/accounting/bulk_customization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      setNotification({
        message: `Note tilføjet til ${targetIds.length} rækker og gemt i Excel!`,
        type: "success",
      });
      setTimeout(() => setNotification(null), 3000);
      setIsBulkNoteOpen(false);
      setBulkNoteInput("");
    } catch (e) {
      console.log("Bulk comment error:", e);
    }
  };

  // Unique vendors for filter
  const vendors = useMemo(() => {
    const vSet = new Set(data.map((d) => d.vendor));
    return ["All", ...Array.from(vSet)];
  }, [data]);

  // Color count summary
  const colorCounts = useMemo(() => {
    const counts: Record<string, number> = {
      All: data.length,
      green: 0,
      yellow: 0,
      blue: 0,
      red: 0,
      purple: 0,
      orange: 0,
      gray: 0,
      none: 0,
      withNotes: 0
    };
    data.forEach(d => {
      const effColor = d.color || (d.status === "Found" ? "green" : (d.status === "Note" ? "yellow" : "none"));
      counts[effColor] = (counts[effColor] || 0) + 1;
      if (d.comment && d.comment.trim().length > 0) {
        counts.withNotes += 1;
      }
    });
    return counts;
  }, [data]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.toLowerCase();
      const matchSearch =
        searchTerm === "" ||
        item.bilag.toString().includes(searchTerm) ||
        item.text.toLowerCase().includes(q) ||
        item.vendor.toLowerCase().includes(q) ||
        (item.filename && item.filename.toLowerCase().includes(q)) ||
        (item.comment && item.comment.toLowerCase().includes(q)) ||
        (item.status_detail && item.status_detail.toLowerCase().includes(q));

      const matchStatus =
        statusFilter === "All" ||
        (statusFilter === "Found" && item.status === "Found") ||
        (statusFilter === "Missing" && item.status === "Missing");

      const matchVendor =
        vendorFilter === "All" || item.vendor === vendorFilter;

      const effectiveColor = item.color || (item.status === "Found" ? "green" : (item.status === "Note" ? "yellow" : "none"));
      const matchColor =
        colorFilter === "All" || effectiveColor === colorFilter;

      const matchNotes =
        !onlyWithNotesFilter || (item.comment && item.comment.trim().length > 0);

      return matchSearch && matchStatus && matchVendor && matchColor && matchNotes;
    });
  }, [data, searchTerm, statusFilter, vendorFilter, colorFilter, onlyWithNotesFilter]);

  return (
    <MainLayout>
      <div className="space-y-6 pb-12">
        {/* Interactive Guided Scraper Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-xs p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-5 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Globe size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{modalProvider} Invoice Downloader</h3>
                    <p className="text-xs text-blue-100 mt-0.5">
                      Account: {activeAccounts[currentAccountIndex]?.name || activeAccounts[0]?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-1 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {modalStep === "PROMPT" && (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                      <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-900 leading-relaxed space-y-2">
                        <p className="font-bold text-sm">Download Instructions:</p>
                        {modalProvider.toLowerCase() === "facebook" ? (
                          <div className="space-y-1.5">
                            <p><b>1.</b> In the opened Meta Business Suite tab, select your date range (e.g. <b>April 1 – May 31, 2026</b> or <b>Q2</b>).</p>
                            <p><b>2.</b> Click <b>Download All / Export Report</b> to download the <code>Transactions.zip</code> into your Downloads folder.</p>
                            <p><b>3.</b> Click <b>Scan & Match Facebook Invoices</b> below — the engine will automatically unzip and pair all 15 reference IDs!</p>
                          </div>
                        ) : modalProvider.toLowerCase() === "godaddy" ? (
                          <div className="space-y-1.5">
                            <p><b>1.</b> Select your target date range or quarter above (e.g. <b>{filterStartDate} to {filterEndDate}</b>).</p>
                            <p><b>2.</b> Click <b>Download Official PDFs (Auto)</b> below.</p>
                            <p><b>3.</b> A Chrome window opens to GoDaddy. The engine automatically paginates through your billing history, executes Chrome's native vector print engine (matching GoDaddy's "Udskriv til PDF" button), saves authentic vector PDFs to your Desktop, and reconciles them to Green in Excel!</p>
                          </div>
                        ) : (
                          <>
                            <p>
                              In your opened {modalProvider} window, make sure you are on the <b>Billing History</b> page.
                            </p>
                            <p>
                              Click <b>Download & Match Invoices (Auto)</b> below to let the Chrome Extension automatically scan and download every invoice on the page!
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Copy Helper Button (Only for ESPs needing console injection) */}
                    {modalProvider.toLowerCase() !== "godaddy" && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between">
                        <div className="text-xs text-gray-600 font-mono truncate max-w-[280px]">
                          document.querySelectorAll('a[href*="/app/document/"]')...
                        </div>
                        <button
                          onClick={handleCopySnippet}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-2xs transition-all cursor-pointer flex-shrink-0"
                        >
                          {copiedScript ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                          {copiedScript ? "Copied!" : "Copy 1-Click Script"}
                        </button>
                      </div>
                    )}

                    {/* Date Range & Quarter Picker */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-700">Filter Quarter / Dates:</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => { setFilterStartDate("2026-01-01"); setFilterEndDate("2026-03-31"); }}
                            className="px-2 py-0.5 text-[10px] font-semibold bg-white hover:bg-slate-200 text-slate-700 rounded border border-slate-300 transition-all cursor-pointer"
                          >
                            Q1
                          </button>
                          <button
                            type="button"
                            onClick={() => { setFilterStartDate("2026-04-01"); setFilterEndDate("2026-06-30"); }}
                            className="px-2 py-0.5 text-[10px] font-semibold bg-white hover:bg-slate-200 text-slate-700 rounded border border-slate-300 transition-all cursor-pointer"
                          >
                            Q2
                          </button>
                          <button
                            type="button"
                            onClick={() => { setFilterStartDate("2026-07-01"); setFilterEndDate("2026-09-30"); }}
                            className="px-2 py-0.5 text-[10px] font-semibold bg-white hover:bg-slate-200 text-slate-700 rounded border border-slate-300 transition-all cursor-pointer"
                          >
                            Q3
                          </button>
                          <button
                            type="button"
                            onClick={() => { setFilterStartDate("2026-04-01"); setFilterEndDate("2026-07-31"); }}
                            className="px-2 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-700 rounded border border-blue-300 transition-all cursor-pointer"
                          >
                            Apr-Jul '26
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] font-medium text-slate-500 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                            className="w-full text-xs px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-slate-500 mb-1">End Date</label>
                          <input
                            type="date"
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                            className="w-full text-xs px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs px-1 pt-1">
                      <span className="font-medium text-gray-500">Automation Engine:</span>
                      {modalProvider.toLowerCase() === "godaddy" ? (
                        <span className="text-blue-700 font-bold text-[11px] flex items-center gap-1 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                          <CheckCircle2 size={11} /> Chrome DevTools Native Vector Print
                        </span>
                      ) : isExtensionConnected ? (
                        <span className="text-emerald-700 font-bold text-[11px] flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          <CheckCircle2 size={11} /> Chrome Extension Active
                        </span>
                      ) : (
                        <span className="text-gray-400 text-[11px]">
                          Extension ready to load
                        </span>
                      )}
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={handleStartDownloadAndMatch}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer ring-2 ring-blue-400"
                      >
                        <Download size={18} />
                        {modalProvider.toLowerCase() === "godaddy" ? `Download Official PDFs (${filterStartDate} to ${filterEndDate})` : "Download & Match Invoices (Auto)"}
                      </button>
                    </div>
                  </div>
                )}

                {modalStep === "DOWNLOADING" && (
                  <div className="py-8 text-center space-y-4">
                    <Loader2 size={40} className="text-blue-600 animate-spin mx-auto" />
                    <div>
                      <h4 className="font-bold text-base text-gray-900">
                        {modalProvider.toLowerCase() === "godaddy"
                          ? `Downloading GoDaddy Vector PDFs (${filterStartDate} to ${filterEndDate})...`
                          : "Scanning & Reconciling Invoices..."}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {modalProvider.toLowerCase() === "godaddy"
                          ? "Chrome window active. Auto-paginating billing history, capturing authentic vector PDFs, and reconciling Excel rows..."
                          : "Extracting dates, amounts, renaming to Bilag numbers, and updating Excel..."}
                      </p>
                    </div>
                  </div>
                )}

                {modalStep === "COMPLETED" && (
                  <div className="space-y-4">
                    {matchedInSession > 0 || data.filter((d) => d.vendor === modalProvider && d.status === "Found").length > 0 ? (
                      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                        <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-sm text-emerald-900">
                            {matchedInSession || data.filter((d) => d.vendor === modalProvider && d.status === "Found").length} Invoices Matched & Reconciled!
                          </h4>
                          <p className="text-xs text-emerald-800 mt-1">
                            Renamed to <b>Bilag_XXX_{modalProvider}.pdf</b> and marked <b>Green</b> in your Excel file.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                        <AlertCircle size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-sm text-amber-900">
                            No New Invoices Detected Yet
                          </h4>
                          <p className="text-xs text-amber-800 mt-1">
                            In your open {modalProvider} window, click <b>Scan & Re-Match</b> (or paste the 1-click script in Console <code>F12</code>) to download all invoices.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-2">
                      {matchedInSession === 0 ? (
                        <button
                          onClick={handleStartDownloadAndMatch}
                          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <RefreshCw size={15} />
                          Scan & Re-Match
                        </button>
                      ) : (
                        <button
                          onClick={handleNextAccount}
                          className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <UserCheck size={16} />
                          Next Account ({activeAccounts[(currentAccountIndex + 1) % activeAccounts.length]?.name.split(" ")[0] || "Next"})
                        </button>
                      )}
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="py-2.5 px-5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs rounded-xl transition-all cursor-pointer"
                      >
                        Done & Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-700 shadow-sm">
              <FileSpreadsheet size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Accounting Bilag & Invoice Matcher
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Press any provider button below to automatically download invoices, match Excel rows, and export green reports
              </p>
            </div>
          </div>

          {/* Export Actions */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={handleDownloadExcel}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
            >
              <FileSpreadsheet size={15} />
              Export Excel (.xlsx)
            </button>
            <button
              onClick={handleDownloadNumberedZip}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all cursor-pointer ring-2 ring-blue-300"
              title="Download ZIP hvor filerne kun hedder {nummer}.pdf (f.eks. 298.pdf)"
            >
              <FolderArchive size={15} />
              Download ZIP (Kun numre: 298.pdf)
            </button>
            <button
              onClick={handleDownloadZip}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
              title="Download ZIP med fulde beskrivende navne"
            >
              <FolderArchive size={15} />
              Download ZIP (Fulde navne)
            </button>
          </div>
        </div>

        {/* Metric KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-gray-200 shadow-sm bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Total In Excel Sheet
                  </p>
                  <h3 className="text-2xl font-extrabold text-gray-900 mt-1">
                    {stats.total_count}
                  </h3>
                </div>
                <div className="p-3 bg-gray-100 text-gray-600 rounded-xl">
                  <FileText size={22} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">All missing accountant lines</p>
            </CardContent>
          </Card>

          <Card className="border-emerald-200 shadow-sm bg-emerald-50/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                    Found & Matched (Green)
                  </p>
                  <h3 className="text-2xl font-extrabold text-emerald-700 mt-1">
                    {stats.found_count}{" "}
                    <span className="text-sm font-medium text-emerald-600">
                      ({Math.round((stats.found_count / (stats.total_count || 1)) * 100)}%)
                    </span>
                  </h3>
                </div>
                <div className="p-3 bg-emerald-100 text-emerald-700 rounded-xl">
                  <CheckCircle2 size={22} />
                </div>
              </div>
              <p className="text-xs text-emerald-700/80 mt-2">
                GoDaddy (31) • Adtraction (12) • AWS (8) • GetResponse (4)
              </p>
            </CardContent>
          </Card>

          <Card className="border-amber-200 shadow-sm bg-amber-50/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                    Pending Download
                  </p>
                  <h3 className="text-2xl font-extrabold text-amber-800 mt-1">
                    {stats.missing_count}
                  </h3>
                </div>
                <div className="p-3 bg-amber-100 text-amber-800 rounded-xl">
                  <AlertCircle size={22} />
                </div>
              </div>
              <p className="text-xs text-amber-800/80 mt-2">
                ESPs (GetResponse, Brevo, FeedBlitz, etc.)
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 shadow-sm bg-blue-50/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                    Missing Amount (DKK)
                  </p>
                  <h3 className="text-2xl font-extrabold text-blue-900 mt-1">
                    {stats.total_missing_amount.toLocaleString("da-DK", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    <span className="text-sm font-normal">DKK</span>
                  </h3>
                </div>
                <div className="p-3 bg-blue-100 text-blue-700 rounded-xl">
                  <ShieldCheck size={22} />
                </div>
              </div>
              <p className="text-xs text-blue-700/80 mt-2">Reconciliation balance</p>
            </CardContent>
          </Card>
        </div>

        {/* 1-Click Automated Provider Action Center */}
        <Card className="border-gray-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  Automated Provider Connectors & Scrapers
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Click any button to open provider billing or sync API records directly.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {/* GoDaddy */}
              <div
                onClick={() => {
                  setVendorFilter("GoDaddy");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-blue-300 bg-blue-50/60 flex flex-col justify-between shadow-2xs cursor-pointer hover:border-blue-500 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-blue-950 group-hover:text-blue-700">GoDaddy (Official)</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                    {data.filter((d) => d.vendor === "GoDaddy" && d.status === "Found").length} / {data.filter((d) => d.vendor === "GoDaddy").length} Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Universal Vector Print • Auto-Paginates History</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("GoDaddy"); }}
                  className="mt-3 w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ring-2 ring-blue-400"
                >
                  <Globe size={14} />
                  Start GoDaddy Scraper
                </button>
              </div>

              {/* Adtraction */}
              <div
                onClick={() => {
                  setVendorFilter("Adtraction");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex flex-col justify-between cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-emerald-700">Adtraction</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    12 / 12 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Publisher API • All matched</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSyncProvider("Adtraction"); }}
                  disabled={isSyncing !== null}
                  className="mt-3 w-full py-1.5 px-3 bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 font-medium text-xs rounded-lg border border-emerald-300 shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Zap size={14} />
                  {isSyncing === "Adtraction" ? "Syncing..." : "Re-Sync Adtraction (API)"}
                </button>
              </div>

              {/* AWS */}
              <div
                onClick={() => {
                  setVendorFilter("AWS");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex flex-col justify-between cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-emerald-700">AWS Billing</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    8 / 8 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Cost Explorer API • All matched</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSyncProvider("AWS"); }}
                  disabled={isSyncing !== null}
                  className="mt-3 w-full py-1.5 px-3 bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 font-medium text-xs rounded-lg border border-emerald-300 shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Zap size={14} />
                  {isSyncing === "AWS" ? "Syncing..." : "Re-Sync AWS (API)"}
                </button>
              </div>

              {/* GetResponse */}
              <div
                onClick={() => {
                  setVendorFilter("GetResponse");
                  setStatusFilter("Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-blue-300 bg-blue-50/60 flex flex-col justify-between shadow-2xs cursor-pointer hover:border-blue-500 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-blue-950 group-hover:text-blue-700">GetResponse</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {data.filter((d) => d.vendor === "GetResponse" && d.status === "Found").length} / 14 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Click to view 1 missing receipt</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("GetResponse"); }}
                  className="mt-3 w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ring-2 ring-blue-400"
                >
                  <Globe size={14} />
                  Start GetResponse Scraper
                </button>
              </div>

              {/* Brevo */}
              <div
                onClick={() => {
                  setVendorFilter("Brevo");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex flex-col justify-between cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-emerald-700">Brevo (SendinBlue)</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {data.filter((d) => d.vendor === "Brevo" && d.status === "Found").length} / 11 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Brevo" && d.status === "Missing").length === 0
                    ? "Multi-Account Scraper • All 11 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Brevo" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Brevo"); }}
                  className="mt-3 w-full py-1.5 px-3 bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 font-medium text-xs rounded-lg border border-emerald-300 shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Check size={14} />
                  Brevo Scraper (11/11)
                </button>
              </div>

              {/* FeedBlitz */}
              <div
                onClick={() => {
                  setVendorFilter("FeedBlitz");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex flex-col justify-between cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-emerald-700">FeedBlitz</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {data.filter((d) => d.vendor === "FeedBlitz" && d.status === "Found").length} / 16 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "FeedBlitz" && d.status === "Missing").length === 0
                    ? "Multi-Account Autonomous Bot • All 16 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "FeedBlitz" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSyncProvider("FeedBlitz"); }}
                  disabled={isSyncing !== null}
                  className="mt-3 w-full py-1.5 px-3 bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 font-medium text-xs rounded-lg border border-emerald-300 shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <RefreshCw size={14} className={isSyncing === "FeedBlitz" ? "animate-spin" : ""} />
                  {isSyncing === "FeedBlitz" ? "Syncing FeedBlitz Bot..." : "Re-Sync FeedBlitz (16/16)"}
                </button>
              </div>

              {/* MailerLite */}
              <div
                onClick={() => {
                  setVendorFilter("MailerLite");
                  setStatusFilter("All");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex flex-col justify-between cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-emerald-700">MailerLite</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {data.filter((d) => d.vendor === "MailerLite" && d.status === "Found").length} / 11 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "MailerLite" && d.status === "Missing").length === 0
                    ? "In-Tab Blob Engine • All 11 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "MailerLite" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("MailerLite"); }}
                  className="mt-3 w-full py-1.5 px-3 bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 font-medium text-xs rounded-lg border border-emerald-300 shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Check size={14} />
                  MailerLite Completed (11/11)
                </button>
              </div>

              {/* Moosend */}
              <div
                onClick={() => {
                  setVendorFilter("Moosend");
                  setStatusFilter(data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0 ? "All" : "Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`p-3.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all group ${
                  data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                    : "border-blue-200 bg-blue-50/40 hover:border-blue-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">Moosend</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {data.filter((d) => d.vendor === "Moosend" && d.status === "Found").length} / 5 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0
                    ? "Kasper Valentin Møller • All 5 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Moosend"); }}
                  className={`mt-3 w-full py-1.5 px-3 font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0
                      ? "bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-300"
                      : "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400"
                  }`}
                >
                  {data.filter((d) => d.vendor === "Moosend" && d.status === "Missing").length === 0 ? (
                    <>
                      <Check size={14} />
                      Moosend Completed (5/5)
                    </>
                  ) : (
                    <>
                      <Globe size={14} />
                      Start Moosend Scraper
                    </>
                  )}
                </button>
              </div>

              {/* Drip.com */}
              <div
                onClick={() => {
                  setVendorFilter("Drip");
                  setStatusFilter(data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0 ? "All" : "Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`p-3.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all group ${
                  data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                    : "border-blue-200 bg-blue-50/40 hover:border-blue-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">Drip</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {data.filter((d) => d.vendor === "Drip" && d.status === "Found").length} / 4 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0
                    ? "Kristoffer Frederik Ryberg • All 4 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Drip"); }}
                  className={`mt-3 w-full py-1.5 px-3 font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0
                      ? "bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-300"
                      : "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400"
                  }`}
                >
                  {data.filter((d) => d.vendor === "Drip" && d.status === "Missing").length === 0 ? (
                    <>
                      <Check size={14} />
                      Drip Completed (4/4)
                    </>
                  ) : (
                    <>
                      <Globe size={14} />
                      Start Drip Scraper
                    </>
                  )}
                </button>
              </div>

              {/* Facebook / Meta Ads */}
              <div
                onClick={() => {
                  setVendorFilter("Facebook");
                  setStatusFilter(data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0 ? "All" : "Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`p-3.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all group ${
                  data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                    : "border-blue-200 bg-blue-50/40 hover:border-blue-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">Facebook (Meta Ads)</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {data.filter((d) => d.vendor === "Facebook" && d.status === "Found").length} / 15 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0
                    ? "Simply Loan Sverige • All 15 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Facebook"); }}
                  className={`mt-3 w-full py-1.5 px-3 font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0
                      ? "bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-300"
                      : "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400"
                  }`}
                >
                  {data.filter((d) => d.vendor === "Facebook" && d.status === "Missing").length === 0 ? (
                    <>
                      <Check size={14} />
                      Facebook Completed (15/15)
                    </>
                  ) : (
                    <>
                      <Globe size={14} />
                      Start Facebook Exporter
                    </>
                  )}
                </button>
              </div>

              {/* Pleo */}
              <div
                onClick={() => {
                  setVendorFilter("Pleo");
                  setStatusFilter(data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0 ? "All" : "Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`p-3.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all group ${
                  data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                    : "border-blue-200 bg-blue-50/40 hover:border-blue-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">Pleo</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {data.filter((d) => d.vendor === "Pleo" && d.status === "Found").length} / 8 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0
                    ? "Kristoffer Frederik Ryberg • All 8 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Pleo"); }}
                  className={`mt-3 w-full py-1.5 px-3 font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0
                      ? "bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-300"
                      : "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400"
                  }`}
                >
                  {data.filter((d) => d.vendor === "Pleo" && d.status === "Missing").length === 0 ? (
                    <>
                      <Check size={14} />
                      Pleo Completed (8/8)
                    </>
                  ) : (
                    <>
                      <Globe size={14} />
                      Start Pleo Scraper
                    </>
                  )}
                </button>
              </div>

              {/* Google One / Google Play */}
              <div
                onClick={() => {
                  setVendorFilter("Google");
                  setStatusFilter(data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0 ? "All" : "Missing");
                  document.getElementById("bilag-table-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`p-3.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all group ${
                  data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400"
                    : "border-blue-200 bg-blue-50/40 hover:border-blue-400"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 group-hover:text-blue-700">Google One</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {data.filter((d) => d.vendor === "Google" && d.status === "Found").length} / 5 Matched
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0
                    ? "Kristoffer Frederik Ryberg • All 5 matched"
                    : `Click to view ${data.filter((d) => d.vendor === "Google" && d.status === "Missing").length} missing rows`}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenScraperModal("Google"); }}
                  className={`mt-3 w-full py-1.5 px-3 font-bold text-xs rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0
                      ? "bg-white hover:bg-emerald-600 hover:text-white text-emerald-700 border border-emerald-300"
                      : "bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400"
                  }`}
                >
                  {data.filter((d) => d.vendor === "Google" && d.status === "Missing").length === 0 ? (
                    <>
                      <Check size={14} />
                      Google Completed (5/5)
                    </>
                  ) : (
                    <>
                      <Globe size={14} />
                      Start Google Scraper
                    </>
                  )}
                </button>
              </div>

              {/* Universal Matcher */}
              <div className="p-3.5 rounded-xl border border-indigo-200 bg-indigo-50/40 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-indigo-950">Universal Matcher</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                    Auto-Watcher
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Scans Downloads & inbox_pdf</p>
                <button
                  onClick={handleRunUniversalMatcher}
                  disabled={isRunningMatcher}
                  className="mt-3 w-full py-1.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-2xs transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <RefreshCw size={14} className={isRunningMatcher ? "animate-spin" : ""} />
                  {isRunningMatcher ? "Scanning & Matching..." : "Run Universal Matcher"}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter and Search Bar */}
        <div id="bilag-table-section" className="space-y-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Status Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-full lg:w-auto overflow-x-auto">
              <button
                onClick={() => { setStatusFilter("All"); setColorFilter("All"); }}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  statusFilter === "All" && colorFilter === "All" && !onlyWithNotesFilter
                    ? "bg-white text-gray-900 shadow-2xs"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Alle bilag ({stats.total_count})
              </button>
              <button
                onClick={() => { setStatusFilter("Found"); setColorFilter("All"); }}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                  statusFilter === "Found"
                    ? "bg-emerald-600 text-white shadow-2xs"
                    : "text-emerald-700 hover:bg-emerald-50"
                }`}
              >
                <CheckCircle2 size={13} />
                Fundet / Grøn ({stats.found_count})
              </button>
              <button
                onClick={() => { setStatusFilter("Missing"); setColorFilter("All"); }}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                  statusFilter === "Missing"
                    ? "bg-amber-600 text-white shadow-2xs"
                    : "text-amber-700 hover:bg-amber-50"
                }`}
              >
                <AlertCircle size={13} />
                Mangler ({stats.missing_count})
              </button>
              <button
                onClick={() => setOnlyWithNotesFilter(!onlyWithNotesFilter)}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                  onlyWithNotesFilter
                    ? "bg-indigo-600 text-white shadow-2xs"
                    : "text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                <MessageSquare size={13} />
                Kun med noter ({colorCounts.withNotes})
              </button>
            </div>

            {/* Search and Vendor Filter */}
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-64">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Søg bilag, tekst, kommentar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <Filter size={15} className="text-gray-400" />
                <select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  className="py-1.5 px-2.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white text-gray-700"
                >
                  {vendors.map((v) => (
                    <option key={v} value={v}>
                      {v === "All" ? "Alle leverandører" : v}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quick Color Filter Bar */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-2 border-t border-gray-100 text-xs">
            <span className="text-gray-400 font-semibold uppercase text-[10px] tracking-wider mr-1 flex-shrink-0 flex items-center gap-1">
              <Palette size={12} /> Farvefilter:
            </span>
            <button
              onClick={() => setColorFilter("All")}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                colorFilter === "All"
                  ? "bg-gray-900 text-white shadow-xs"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Alle ({data.length})
            </button>
            {(["green", "yellow", "blue", "red", "purple", "orange", "gray", "none"] as RowColor[]).map((col) => {
              const cfg = COLOR_CONFIG[col];
              const count = colorCounts[col] || 0;
              const isSelected = colorFilter === col;
              return (
                <button
                  key={col}
                  onClick={() => setColorFilter(col)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 border ${
                    isSelected
                      ? "ring-2 ring-indigo-500 font-bold " + cfg.bgBadge + " " + cfg.textBadge
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                  <span>{cfg.label}</span>
                  <span className="text-[10px] opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bilag Data Table */}
        <Card className="border-gray-200 shadow-sm overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-gray-600 border-collapse">
              <thead className="bg-gray-50/90 text-gray-700 border-b border-gray-200 font-semibold select-none">
                <tr>
                  {/* Select All Checkbox */}
                  <th className="py-3 px-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={selectedBilags.length === filteredRows.length && filteredRows.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer w-4 h-4"
                      title="Vælg alle synlige rækker"
                    />
                  </th>
                  <th className="py-3 px-3 w-28">Farve</th>
                  <th className="py-3 px-3 w-20">Status</th>
                  <th className="py-3 px-3 w-16">Bilag #</th>
                  <th className="py-3 px-3 w-24">Dato</th>
                  <th className="py-3 px-4 min-w-[220px]">Bank / Transaktionstekst</th>
                  <th className="py-3 px-3 w-28">Leverandør</th>
                  <th className="py-3 px-3 w-24 text-right">Beløb</th>
                  <th className="py-3 px-4 w-52">Matchet Bilag PDF</th>
                  <th className="py-3 px-4 min-w-[240px]">Kommentar / Revisor-note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Search size={24} className="text-gray-300" />
                        <p className="text-sm font-medium">Ingen bilag matcher dine filtre.</p>
                        <button
                          onClick={() => { setSearchTerm(""); setStatusFilter("All"); setColorFilter("All"); setVendorFilter("All"); setOnlyWithNotesFilter(false); setSelectedBilags([]); }}
                          className="mt-1 text-xs text-indigo-600 hover:underline cursor-pointer"
                        >
                          Nulstil alle filtre
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const isFound = row.status === "Found";
                    const isNote = row.status === "Note";
                    const effColor: RowColor = row.color || (isFound ? "green" : (isNote ? "yellow" : "none"));
                    const colorCfg = COLOR_CONFIG[effColor] || COLOR_CONFIG.none;
                    const isEditingThis = editingBilag === row.bilag;
                    const isColorMenuOpen = activeColorMenuBilag === row.bilag;
                    const isSelected = selectedBilags.includes(row.bilag);

                    return (
                      <tr
                        key={row.bilag}
                        className={`transition-colors ${colorCfg.rowBg} ${colorCfg.borderLeft} group relative ${
                          isSelected ? "ring-1 ring-inset ring-indigo-400 bg-indigo-50/40" : ""
                        }`}
                      >
                        {/* 0. Row Checkbox */}
                        <td className="py-2.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(row.bilag)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer w-4 h-4"
                          />
                        </td>

                        {/* 1. Direct Quick-Color Palette & Dropdown */}
                        <td className="py-2 px-3 relative">
                          <div className="flex items-center gap-1">
                            {/* Main Color Indicator Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveColorMenuBilag(isColorMenuOpen ? null : row.bilag);
                              }}
                              title={`Klik for fuld farvemenu (${colorCfg.label})`}
                              className="p-1 rounded-md hover:bg-black/10 transition-all cursor-pointer flex items-center gap-1.5 flex-shrink-0"
                            >
                              <span className={`w-3.5 h-3.5 rounded-full shadow-2xs ${colorCfg.dot}`} />
                              <span className="text-[11px] font-medium text-gray-700 hidden xl:inline">
                                {colorCfg.label}
                              </span>
                            </button>

                            {/* Direct 1-Click Quick Color Dots on Row */}
                            <div className="flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                              {(["green", "yellow", "blue", "red"] as RowColor[]).map((c) => {
                                const cCfg = COLOR_CONFIG[c];
                                if (c === effColor) return null;
                                return (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateRowCustomization(row.bilag, undefined, c);
                                    }}
                                    title={`Farv ${cCfg.label}`}
                                    className={`w-3 h-3 rounded-full hover:scale-130 transition-transform cursor-pointer ${cCfg.dot}`}
                                  />
                                );
                              })}
                            </div>
                          </div>

                          {/* Full Color Dropdown Popover */}
                          {isColorMenuOpen && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="absolute left-6 top-1 z-30 bg-white rounded-xl shadow-2xl border border-gray-200 p-2 w-52 text-left animate-in fade-in zoom-in-95"
                            >
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 py-1 border-b border-gray-100 flex items-center justify-between">
                                <span>Vælg farve</span>
                                <button
                                  onClick={() => setActiveColorMenuBilag(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              <div className="py-1 space-y-0.5 max-h-60 overflow-y-auto">
                                {(["green", "yellow", "blue", "red", "purple", "orange", "gray", "none"] as RowColor[]).map((c) => {
                                  const cfg = COLOR_CONFIG[c];
                                  const isCurrent = effColor === c;
                                  return (
                                    <button
                                      key={c}
                                      type="button"
                                      onClick={() => {
                                        handleUpdateRowCustomization(row.bilag, undefined, c);
                                        setActiveColorMenuBilag(null);
                                      }}
                                      className={`w-full px-2 py-1.5 text-xs rounded-lg flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer ${
                                        isCurrent ? "font-bold bg-gray-50 text-indigo-900" : "text-gray-700"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className={`w-3.5 h-3.5 rounded-full ${cfg.dot}`} />
                                        <span>{cfg.label}</span>
                                      </div>
                                      {isCurrent && <Check size={13} className="text-indigo-600" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </td>

                        {/* 2. Status Badge */}
                        <td className="py-2.5 px-3">
                          {isFound ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle2 size={11} />
                              Fundet
                            </span>
                          ) : isNote ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                              <Tag size={11} />
                              Note
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-800 border border-rose-200">
                              <AlertCircle size={11} />
                              Mangler
                            </span>
                          )}
                        </td>

                        {/* 3. Bilag # */}
                        <td className="py-2.5 px-3 font-bold text-gray-900">
                          {row.bilag}
                        </td>

                        {/* 4. Date */}
                        <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap">
                          {row.date}
                        </td>

                        {/* 5. Description Text */}
                        <td className="py-2.5 px-4 font-medium text-gray-800">
                          {row.text}
                        </td>

                        {/* 6. Vendor Tag */}
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-white/80 border border-gray-200 text-gray-700 whitespace-nowrap">
                            {row.vendor}
                          </span>
                        </td>

                        {/* 7. Amount */}
                        <td className="py-2.5 px-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                          {Number(row.amount).toLocaleString("da-DK", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          <span className="text-[10px] text-gray-500 font-normal">
                            {row.currency || "DKK"}
                          </span>
                        </td>

                        {/* 8. Matched File */}
                        <td className="py-2.5 px-4">
                          {row.filename ? (
                            <div className="flex items-center gap-1.5" title={row.filename}>
                              <FileText size={13} className="text-emerald-600 flex-shrink-0" />
                              <span className="text-xs font-mono text-emerald-900 truncate max-w-[170px]">
                                {row.filename}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-[11px]">
                              Ingen PDF tilknyttet
                            </span>
                          )}
                        </td>

                        {/* 9. Interactive Comment / Note Column */}
                        <td className="py-2 px-4">
                          {isEditingThis ? (
                            <div className="space-y-2 py-1 bg-white p-2.5 rounded-lg border border-indigo-300 shadow-md">
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  autoFocus
                                  placeholder="Skriv kommentar til Jesper..."
                                  value={tempComment}
                                  onChange={(e) => setTempComment(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleUpdateRowCustomization(row.bilag, tempComment);
                                      setEditingBilag(null);
                                    } else if (e.key === "Escape") {
                                      setEditingBilag(null);
                                    }
                                  }}
                                  className="w-full text-xs px-2.5 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleUpdateRowCustomization(row.bilag, tempComment);
                                    setEditingBilag(null);
                                  }}
                                  title="Gem note (Enter)"
                                  className="p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-all cursor-pointer flex-shrink-0"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingBilag(null)}
                                  title="Annullér (Esc)"
                                  className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-all cursor-pointer flex-shrink-0"
                                >
                                  <X size={14} />
                                </button>
                              </div>

                              {/* Quick Note Presets */}
                              <div className="flex flex-wrap gap-1">
                                {NOTE_PRESETS.map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => setTempComment(preset)}
                                    className="text-[10px] px-1.5 py-0.5 bg-gray-100 hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 rounded transition-colors cursor-pointer"
                                  >
                                    + {preset}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() => {
                                setEditingBilag(row.bilag);
                                setTempComment(row.comment || row.status_detail || "");
                              }}
                              className="cursor-pointer group/note flex items-center justify-between gap-2 min-h-[28px] py-0.5 px-2 rounded-md hover:bg-black/5 transition-colors"
                            >
                              {row.comment ? (
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <MessageSquare size={12} className="text-indigo-600 flex-shrink-0" />
                                  <span className="text-xs text-gray-800 truncate" title={row.comment}>
                                    {row.comment}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[11px] text-gray-400 italic flex items-center gap-1 group-hover/note:text-indigo-600">
                                  <Plus size={11} /> Tilføj kommentar / note
                                </span>
                              )}
                              <Edit3
                                size={12}
                                className="text-gray-400 opacity-0 group-hover/note:opacity-100 transition-opacity text-indigo-600 flex-shrink-0"
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Sticky Floating Bulk Action Bar */}
        {selectedBilags.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-2xl shadow-2xl px-5 py-3 border border-gray-700 flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-bottom-4 max-w-4xl w-full mx-auto justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold bg-indigo-600 px-2.5 py-1 rounded-full text-white">
                {selectedBilags.length} valgt
              </span>
              <span className="text-xs text-gray-300 font-medium hidden sm:inline">
                Vælg handling for valgte rækker:
              </span>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Quick Bulk Color Buttons */}
              <button
                type="button"
                onClick={() => handleBulkSetColor("green")}
                className="px-2.5 py-1 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                Grøn
              </button>
              <button
                type="button"
                onClick={() => handleBulkSetColor("yellow")}
                className="px-2.5 py-1 bg-amber-700 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                Gul
              </button>
              <button
                type="button"
                onClick={() => handleBulkSetColor("blue")}
                className="px-2.5 py-1 bg-sky-700 hover:bg-sky-600 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-sky-300" />
                Blå
              </button>
              <button
                type="button"
                onClick={() => handleBulkSetColor("red")}
                className="px-2.5 py-1 bg-rose-700 hover:bg-rose-600 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                Rød
              </button>
              <button
                type="button"
                onClick={() => handleBulkSetColor("none")}
                className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-all cursor-pointer"
              >
                Nulstil
              </button>

              {/* Bulk Note Modal Trigger */}
              <button
                type="button"
                onClick={() => setIsBulkNoteOpen(true)}
                className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
              >
                <MessageSquare size={13} />
                Tilføj note
              </button>

              {/* Clear selection */}
              <button
                type="button"
                onClick={() => setSelectedBilags([])}
                className="p-1 hover:bg-gray-800 rounded-md text-gray-400 hover:text-white transition-colors cursor-pointer ml-1"
                title="Ryd valg"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Bulk Note Modal */}
        {isBulkNoteOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-xs p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200">
              <div className="bg-indigo-600 p-4 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare size={18} />
                  <h4 className="font-bold text-sm">Tilføj note til {selectedBilags.length} rækker</h4>
                </div>
                <button
                  onClick={() => setIsBulkNoteOpen(false)}
                  className="text-white/80 hover:text-white cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <input
                  type="text"
                  autoFocus
                  placeholder="Indtast fælles kommentar..."
                  value={bulkNoteInput}
                  onChange={(e) => setBulkNoteInput(e.target.value)}
                  className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />

                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Eller vælg en hurtig note:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {NOTE_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setBulkNoteInput(preset)}
                        className="text-xs px-2 py-1 bg-gray-100 hover:bg-indigo-50 hover:text-indigo-700 text-gray-700 rounded-md transition-colors cursor-pointer"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsBulkNoteOpen(false)}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    Annullér
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkSetNote(bulkNoteInput)}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all cursor-pointer"
                  >
                    Gem for alle {selectedBilags.length} rækker
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
